
import React, { useState, useCallback } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, ChevronsUpDown, Loader2, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface CategorySelectProps {
  categories: { id: string; name: string }[];
  selected: string[];
  onChange: (value: string[]) => void;
  isLoading?: boolean;
}

export function CategorySelect({ 
  categories = [], 
  selected = [], 
  onChange,
  isLoading = false
}: CategorySelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Ensure categories and selected are arrays, even if they're undefined
  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeSelected = Array.isArray(selected) ? selected : [];

  const toggleCategory = useCallback((categoryId: string) => {
    const newSelection = safeSelected.includes(categoryId)
      ? safeSelected.filter(id => id !== categoryId)
      : [...safeSelected, categoryId];
    onChange(newSelection);
  }, [safeSelected, onChange]);

  const removeCategory = useCallback((categoryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(safeSelected.filter(id => id !== categoryId));
  }, [safeSelected, onChange]);

  // Filter categories based on search query
  const filteredCategories = safeCategories.filter(category => 
    category?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <Button
        variant="outline"
        role="combobox"
        className="w-full justify-between"
        disabled
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="ml-2">Loading categories...</span>
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between border-primary hover:bg-primary/5 focus:ring-primary"
        >
          <span className="truncate">
            {safeSelected.length > 0 ? `${safeSelected.length} selected` : "Select categories..."}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-primary" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-4">
        <div className="space-y-4">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-primary" />
            <Input
              placeholder="Search categories..."
              className="pl-8 border-primary focus-visible:ring-primary"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          {/* Category list with checkboxes */}
          <ScrollArea className="h-64">
            {filteredCategories.length > 0 ? (
              <div className="space-y-2">
                {filteredCategories.map((category) => (
                  <div key={category?.id || 'unknown'} className="flex items-center space-x-2">
                    <Checkbox
                      id={`category-${category?.id}`}
                      checked={category?.id ? safeSelected.includes(category.id) : false}
                      onCheckedChange={() => category?.id && toggleCategory(category.id)}
                      className="border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                    />
                    <label
                      htmlFor={`category-${category?.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {category?.name || 'Unnamed category'}
                    </label>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-2 text-sm text-muted-foreground text-center">
                No categories found
              </div>
            )}
          </ScrollArea>
        </div>
      </PopoverContent>
      
      {/* Selected categories badges */}
      {safeSelected.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {safeSelected.map((categoryId) => {
            const category = safeCategories.find(c => c?.id === categoryId);
            if (!category) return null;
            return (
              <Badge key={categoryId} variant="outline" className="flex items-center gap-1 border-primary bg-primary/10 text-primary">
                {category.name || 'Unnamed category'}
                <X
                  className="h-3 w-3 cursor-pointer text-primary"
                  onClick={(e) => removeCategory(categoryId, e)}
                />
              </Badge>
            );
          })}
        </div>
      )}
    </Popover>
  );
}
